import React, {useState, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import {
    MessageCircle,
    Download,
    Loader2,
    Copy,
    AlertTriangle,
    RefreshCw,
    CheckCircle,
    XCircle,
    Search,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

const ViewQalamChatsPage = () => {
    const [conversations, setConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [copiedId, setCopiedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchQalamChats();
    }, [page, pageSize]);

    const fetchQalamChats = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/qalam_conversations/?page=${page}&page_size=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch chats: ${response.statusText}`);
            }

            const data = await response.json();
            setConversations(data);
        } catch (error) {
            console.error('Error fetching qalam chats:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteQalamChat = async (chatId) => {
        const confirmed = window.confirm('Are you sure you want to delete this chat?');
        if (!confirmed) return;

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/delete_qalam_conversation/${chatId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to delete chat: ${response.statusText}`);
            }

            // Remove the deleted chat from the state
            setConversations(prevConversations =>
                prevConversations.filter(chat => chat.chat_id !== chatId)
            );
        } catch (error) {
            console.error('Error deleting chat:', error);
            alert(`Failed to delete chat: ${error.message}`);
        }
    };

    const copyToClipboard = (text, chatId) => {
        const plainText = text.replace(/[*#_`]/g, ''); // Remove markdown characters
        navigator.clipboard.writeText(plainText)
            .then(() => {
                setCopiedId(chatId);
                setTimeout(() => setCopiedId(null), 2000);
            })
            .catch(err => {
                console.error('Failed to copy:', err);
            });
    };

    const downloadQalamCSV = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/export-qalam-chats-to-csv/?page=${page}&page_size=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to download CSV: ${response.statusText}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `digital_qalam_chats.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert(`Failed to download CSV: ${error.message}`);
        }
    };

    const filteredConversations = conversations.filter(chat =>
        chat.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <MessageCircle className="text-blue-600" size={32}/>
                    View Digital Qalam Chats
                </h1>
                <p className="mt-2 text-gray-600">
                    Browse, search, and manage Digital Qalam user conversations
                </p>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="page" className="text-sm font-medium text-gray-700">
                                Page:
                            </label>
                            <input
                                id="page"
                                type="number"
                                min="1"
                                value={page}
                                onChange={(e) => setPage(Math.max(1, Number(e.target.value)))}
                                className="w-16 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">
                                Results:
                            </label>
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {[10, 25, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        <div className="relative w-full sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400"/>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search chats..."
                                className="pl-10 w-full border border-gray-300 rounded-md py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button
                            onClick={fetchQalamChats}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>

                        <button
                            onClick={downloadQalamCSV}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                        >
                            <Download size={14} className="mr-1"/>
                            Export CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Chats Table */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading chats</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Question
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Answer
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredConversations.length > 0 ? (
                                filteredConversations.map((chat) => (
                                    <tr key={chat.chat_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900  max-w-[150px]">
                                                {chat.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div
                                                className="text-sm text-gray-900 max-h-20 overflow-y-auto max-w-[200px]">
                                                {chat.question}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div
                                                className="text-sm text-gray-900 max-h-24 overflow-y-auto prose prose-sm max-w-[250px]">
                                                <ReactMarkdown>{chat.answer}</ReactMarkdown>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500">
                                                {new Date(chat.timestamp).toLocaleString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-3">
                                                <button
                                                    onClick={() => copyToClipboard(chat.answer, chat.chat_id)}
                                                    className="p-1 rounded-full text-blue-500 hover:text-blue-600"
                                                    title="Copy answer"
                                                >
                                                    {copiedId === chat.chat_id ? (
                                                        <CheckCircle size={18}/>
                                                    ) : (
                                                        <Copy size={18}/>
                                                    )}
                                                </button>

                                                <button
                                                    onClick={() => deleteQalamChat(chat.chat_id)}
                                                    className="p-1 rounded-full text-red-500 hover:text-red-600"
                                                    title="Delete chat"
                                                >
                                                    <XCircle size={18}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center text-gray-500">
                                        {searchTerm ? (
                                            <div className="flex flex-col items-center">
                                                <Search size={40} className="text-gray-400 mb-2"/>
                                                <p>No chats found matching "{searchTerm}"</p>
                                                <button
                                                    onClick={() => setSearchTerm('')}
                                                    className="mt-2 text-blue-600 hover:underline"
                                                >
                                                    Clear search
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center">
                                                <MessageCircle size={40} className="text-gray-400 mb-2"/>
                                                <p>No chats available</p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={filteredConversations.length < pageSize}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{filteredConversations.length}</span> results
                                    {pageSize < filteredConversations.length && (
                                        <> (limited to <span className="font-medium">{pageSize}</span> per page)</>
                                    )}
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                     aria-label="Pagination">
                                    <button
                                        onClick={() => setPage(Math.max(1, page - 1))}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <ChevronLeft className="h-5 w-5" aria-hidden="true"/>
                                    </button>
                                    <span
                                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page}
                  </span>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={filteredConversations.length < pageSize}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Next</span>
                                        <ChevronRight className="h-5 w-5" aria-hidden="true"/>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewQalamChatsPage;